/**
 * Central de Integrações: cada provedor com a sua configuração, os segredos no cofre do painel e o botão
 * "Testar conexão". Nada disso exige mexer no servidor: o worker lê tudo do banco a cada uso.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { from, rpc, unwrap, useAction, useHasRole, useOrg, useSession } from "@vixio/data";
import {
  Button, Callout, DataTable, NumberInput, Panel, Select, StatusPill, TextInput, formatDateTime,
} from "@vixio/ui";
import type {} from "@vixio/core-ui";
import type { Provider } from "@vixio/notifications-ui";
import { SECRET_GROUP, type SystemSecret } from "./api";
import { ConnectionTestButton, SecretField, useSystemSecrets } from "./secrets";

// ---------------------------------------------------------------- utilitários

function useModuleOn(name: string): boolean {
  const s = useSession();
  const m = s.modules.find((x) => x.name === name);
  return Boolean(m?.installed && m.enabled);
}

/** Configuração da organização (texto). */
function useSetting(key: string) {
  const org = useOrg();
  return useQuery({
    queryKey: ["integrations", "setting", org.id, key],
    queryFn: async () => {
      const v = await rpc("core", "get_setting", { p_org: org.id, p_key: key });
      return v === null || v === undefined ? null : v;
    },
  });
}

function useSaveSetting(key: string, success: string) {
  const org = useOrg();
  return useAction((value: unknown) => rpc("core", "set_setting", { p_org: org.id, p_key: key, p_value: value ?? null }), {
    invalidate: [["integrations", "setting", org.id, key], ["core"]],
    success,
  });
}

function useDefaultProvider(channel: "email" | "whatsapp", kind: string, enabled: boolean) {
  const org = useOrg();
  return useQuery({
    queryKey: ["notifications", "providers", org.id, "central", channel],
    enabled,
    queryFn: async () => {
      const rows = await unwrap<Provider[]>(
        from("notifications", "providers").select("*").eq("org_id", org.id).eq("channel", channel).eq("kind", kind)
          .order("is_default", { ascending: false }).order("created_at"),
      );
      return rows[0] ?? null;
    },
  });
}

function AdminOnly() {
  return <Callout tone="info">Só administradores configuram integrações e segredos.</Callout>;
}

/** Endereço público do worker, necessário para montar as URLs de webhook que se colam nos provedores. */
function WorkerUrl({ path, label }: { path: string; label: string }) {
  const url = useSetting("worker.public_url");
  const [v, setV] = useState("");
  const save = useSaveSetting("worker.public_url", "Endereço do worker salvo");
  const base = typeof url.data === "string" ? url.data.replace(/\/+$/, "") : "";
  if (url.isLoading) return null;
  if (!base) {
    return (
      <form className="vx-row" style={{ alignItems: "flex-end" }} onSubmit={(e) => { e.preventDefault(); if (/^https:\/\//.test(v.trim())) save.mutate(v.trim()); }}>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <TextInput label="Endereço público do worker" value={v} onChange={setV} placeholder="https://hooks.vixio.com.br" className="mono"
            hint="Onde o worker recebe webhooks. Uma vez só; também em Configurações → Gerais." />
        </div>
        <Button type="submit" loading={save.isPending} disabled={!/^https:\/\//.test(v.trim())}>Salvar</Button>
      </form>
    );
  }
  return (
    <div className="vx-field">
      <span className="vx-field__label">{label}</span>
      <input className="vx-input mono" readOnly value={`${base}${path}`} onFocus={(e) => e.currentTarget.select()} aria-label={label} data-testid="webhook-url" />
    </div>
  );
}

// ---------------------------------------------------------------- E-mail

export function EmailTab() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const on = useModuleOn("notifications");
  const prov = useDefaultProvider("email", "smtp", isAdmin && on);
  const [f, setF] = useState({ host: "", port: 587 as number | null, security: "starttls", username: "", from: "" });
  useEffect(() => {
    const c = prov.data?.config ?? {};
    setF({ host: String(c.host ?? ""), port: Number(c.port ?? 587), security: String(c.security ?? "starttls"), username: String(c.username ?? ""), from: String(c.from ?? "") });
  }, [prov.data]);
  const port25 = f.port === 25;
  const ok = f.host.trim() && f.from.trim() && !port25;
  const save = useAction(
    () => rpc("notifications", "upsert_provider", {
      p_org: org.id, p_channel: "email", p_kind: "smtp", p_name: prov.data?.name ?? "E-mail (SMTP)",
      p_config: { host: f.host.trim(), port: f.port ?? 587, security: f.security, from: f.from.trim(), ...(f.username.trim() ? { username: f.username.trim() } : {}) },
      p_secret_ref: "vault:smtp.password", p_is_default: true, p_active: true, p_id: prov.data?.id ?? null,
    }),
    { invalidate: [["notifications"]], success: "Servidor de e-mail salvo" },
  );
  if (!isAdmin) return <AdminOnly />;
  if (!on) return <Callout tone="info">O módulo de avisos (notifications) não está ligado nesta organização.</Callout>;
  return (
    <div className="vx-stack vx-stack--lg">
      <Callout tone="warn" title="Porta 25 bloqueada na hospedagem">Use 587 (STARTTLS) ou 465 (TLS direto). A mesma configuração envia avisos, códigos de assinatura, convites e os e-mails de login.</Callout>
      <Panel title="Servidor de e-mail (SMTP)" actions={<ConnectionTestButton provider="smtp" />}>
        <form className="vx-stack" onSubmit={(e) => { e.preventDefault(); if (ok) save.mutate(undefined); }}>
          <div className="vx-form-grid">
            <TextInput label="Servidor" value={f.host} onChange={(v) => setF({ ...f, host: v })} required className="mono" placeholder="smtp.exemplo.com.br" />
            <NumberInput label="Porta" value={f.port} onChange={(v) => setF({ ...f, port: v })} min={1} max={65535} error={port25 ? "A porta 25 é bloqueada: use 587 ou 465." : undefined} />
            <Select label="Segurança" value={f.security} onChange={(v) => setF({ ...f, security: v, port: v === "tls" ? 465 : v === "starttls" ? 587 : f.port })}
              options={[{ value: "starttls", label: "STARTTLS (587)" }, { value: "tls", label: "TLS direto (465)" }, { value: "none", label: "Sem criptografia (só rede local)" }]} />
            <TextInput label="Usuário" value={f.username} onChange={(v) => setF({ ...f, username: v })} className="mono" autoComplete="off" />
            <TextInput className="span-2" label="Remetente" value={f.from} onChange={(v) => setF({ ...f, from: v })} required hint="Ex.: Vixio <nao-responda@vixio.com.br>. O domínio precisa de SPF, DKIM e DMARC." />
          </div>
          <div className="vx-form-actions">
            <Button type="submit" variant="primary" loading={save.isPending} disabled={!ok}>Salvar servidor</Button>
          </div>
        </form>
        <div className="vx-section">
          <SecretField secretKey="smtp.password" label="Senha do SMTP" hint="Senha da conta ou senha de app. Fica cifrada no banco; só o worker lê." />
        </div>
      </Panel>
      <Callout tone="info" title="DNS sugerido para o domínio do remetente">
        SPF: <span className="mono">v=spf1 include:&lt;domínio do provedor&gt; ~all</span> · DKIM: o registro que o provedor fornecer ·
        DMARC: <span className="mono">v=DMARC1; p=quarantine; rua=mailto:dmarc@&lt;seu domínio&gt;</span>. Outros provedores (SMS, push, SMTP extra) em <Link to="/configuracoes/avisos/provedores">Provedores de aviso</Link>.
      </Callout>
    </div>
  );
}

// ---------------------------------------------------------------- Cobrança

const CHARGE_KIND: Record<string, string> = { pix: "Pix", boleto: "Boleto", card: "Cartão" };

export function BillingTab() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const charge = useSetting("billing.default_charge");
  const envSetting = useSetting("billing.asaas_environment");
  const baseSetting = useSetting("billing.asaas_base_url");
  const cur = (charge.data ?? null) as { provider?: string; kind?: string } | null;
  const [provider, setProvider] = useState("");
  const [kind, setKind] = useState("pix");
  const [envv, setEnvv] = useState("sandbox");
  const [base, setBase] = useState("");
  useEffect(() => { setProvider(cur?.provider ?? ""); setKind(cur?.kind ?? "pix"); }, [charge.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setEnvv(typeof envSetting.data === "string" ? envSetting.data : "sandbox"), [envSetting.data]);
  useEffect(() => setBase(typeof baseSetting.data === "string" ? baseSetting.data : ""), [baseSetting.data]);
  const saveCharge = useSaveSetting("billing.default_charge", "Cobrança automática salva");
  const saveEnv = useSaveSetting("billing.asaas_environment", "Ambiente do Asaas salvo");
  const saveBase = useSaveSetting("billing.asaas_base_url", "Endereço do Asaas salvo");
  if (!isAdmin) return <AdminOnly />;
  return (
    <div className="vx-stack vx-stack--lg">
      <Panel title="Cobrança automática padrão">
        <div className="vx-row" style={{ alignItems: "flex-end" }}>
          <Select label="Provedor" value={provider} onChange={setProvider} placeholder="Sem cobrança automática"
            options={[{ value: "asaas", label: "Asaas" }]} />
          <Select label="Forma" value={kind} onChange={setKind} options={Object.entries(CHARGE_KIND).map(([value, label]) => ({ value, label }))} disabled={!provider} />
          <Button variant="primary" loading={saveCharge.isPending} onClick={() => saveCharge.mutate(provider ? { provider, kind } : null)}>Salvar</Button>
        </div>
        <p className="muted" style={{ marginTop: 6, fontSize: "var(--fs-12)" }}>Cada conta a receber gera uma cobrança com este provedor e forma (billing.default_charge).</p>
      </Panel>
      <Panel title="Asaas" actions={<ConnectionTestButton provider="asaas" />}>
        <div className="vx-stack">
          <div className="vx-row" style={{ alignItems: "flex-end" }}>
            <Select label="Ambiente" value={envv} onChange={(v) => { setEnvv(v); saveEnv.mutate(v); }}
              options={[{ value: "sandbox", label: "Sandbox (testes)" }, { value: "producao", label: "Produção" }]} />
            <div style={{ flex: "1 1 280px", minWidth: 0 }}>
              <TextInput label="Endereço da API (opcional)" value={base} onChange={setBase} className="mono" placeholder="vazio = oficial do ambiente" />
            </div>
            <Button onClick={() => saveBase.mutate(base.trim() || null)} loading={saveBase.isPending} disabled={Boolean(base.trim()) && !/^https:\/\//.test(base.trim())}>Salvar endereço</Button>
          </div>
          <SecretField secretKey="asaas.api_key" label="Chave da API do Asaas" hint="Em Asaas → Integrações → Chave de API. Substitui ASAAS_API_KEY." />
          <SecretField secretKey="asaas.webhook_token" label="Token do webhook" mode="generate"
            hint="Cole este token no campo 'Token de autenticação' do webhook de cobranças do Asaas. Substitui ASAAS_WEBHOOK_TOKEN." />
          <WorkerUrl path={`/hooks/billing/asaas/${org.slug}`} label="URL do webhook (cole no Asaas)" />
        </div>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------- NFS-e

export function FiscalTab() {
  const isAdmin = useHasRole("admin");
  const on = useModuleOn("fiscal");
  if (!isAdmin) return <AdminOnly />;
  return (
    <div className="vx-stack">
      <Callout tone="info" title="Segredos da NFS-e por emissor">
        Token do provedor, segredo do webhook e certificado A1 (com a senha) ficam no cofre e são definidos direto na tela
        {" "}{on ? <Link to="/configuracoes/fiscal">Configuração fiscal</Link> : "Configuração fiscal (módulo fiscal desligado)"}, em cada CNPJ, com o botão Testar conexão.
      </Callout>
      <WorkerUrl path="/hooks/fiscal/<provedor>" label="URL dos webhooks de NFS-e (troque <provedor> por focus_nfe, nfe_io…)" />
    </div>
  );
}

// ---------------------------------------------------------------- WhatsApp e atendimento

export function HubTab() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const on = useModuleOn("notifications");
  const prov = useDefaultProvider("whatsapp", "hub_whatsapp", isAdmin && on);
  const [url, setUrl] = useState("");
  useEffect(() => setUrl(String(prov.data?.config.base_url ?? "")), [prov.data]);
  const okUrl = /^https:\/\/[^/\s]+/.test(url.trim());
  const save = useAction(
    () => rpc("notifications", "upsert_provider", {
      p_org: org.id, p_channel: "whatsapp", p_kind: "hub_whatsapp", p_name: prov.data?.name ?? "WhatsApp (atendimento)",
      p_config: { base_url: url.trim().replace(/\/+$/, "") }, p_secret_ref: "vault:hub.api_token", p_is_default: true, p_active: true, p_id: prov.data?.id ?? null,
    }),
    { invalidate: [["notifications"]], success: "Atendimento salvo" },
  );
  if (!isAdmin) return <AdminOnly />;
  return (
    <div className="vx-stack vx-stack--lg">
      <Panel title="WhatsApp pelo atendimento (DeskcommCRM)" actions={<ConnectionTestButton provider="hub" />}>
        <div className="vx-stack">
          {on ? (
            <form className="vx-row" style={{ alignItems: "flex-end" }} onSubmit={(e) => { e.preventDefault(); if (okUrl) save.mutate(undefined); }}>
              <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                <TextInput label="Endereço do atendimento" value={url} onChange={setUrl} className="mono" placeholder="https://atende.vixio.com.br" hint="API /api/v1 do DeskcommCRM." />
              </div>
              <Button type="submit" variant="primary" loading={save.isPending} disabled={!okUrl}>Salvar</Button>
            </form>
          ) : <Callout tone="info">Ligue o módulo de avisos para enviar WhatsApp.</Callout>}
          <SecretField secretKey="hub.api_token" label="Token da API do atendimento" hint="Token Bearer (dsk_…). Substitui HUB_API_TOKEN." />
        </div>
      </Panel>
      <Panel title="Ponte com o atendimento (leads)">
        <div className="vx-stack">
          <SecretField secretKey="hub.inbound_secret" label="Segredo dos avisos do atendimento" mode="generate"
            hint="Configure no atendimento, na ação 'chamar webhook'. Substitui HUB_INBOUND_SECRET." />
          <WorkerUrl path={`/hooks/hub/${org.slug}`} label="URL dos avisos do atendimento (ação chamar webhook)" />
          <SecretField secretKey="hub.webhook_secret" label="Segredo da captação de leads" mode="generate"
            hint="O Vixio assina os leads enviados à URL de captação do atendimento. Ao ligar a ponte, ela passa a usar este segredo (substitui HUB_WEBHOOK_SECRET)." />
        </div>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------- IA

export function AiTab() {
  const isAdmin = useHasRole("admin");
  if (!isAdmin) return <AdminOnly />;
  return (
    <Panel title="OpenRouter" actions={<ConnectionTestButton provider="openrouter" />}>
      <div className="vx-stack">
        <SecretField secretKey="openrouter.api_key" label="Chave do OpenRouter" hint="Usada pela central de IA (orçamento, guardrails e prompts ficam em Central de IA)." />
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------- Google (client secret)

export function GoogleSecretPanel() {
  return (
    <div className="vx-stack" style={{ marginTop: 12 }}>
      <SecretField secretKey="google.client_secret" label="Client secret" hint="Par do client ID. Fica cifrado no banco; usado na troca do código do OAuth." />
      <ConnectionTestButton provider="google" />
    </div>
  );
}

// ---------------------------------------------------------------- Cofre (todos os segredos)

export function VaultTab() {
  const isAdmin = useHasRole("admin");
  const list = useSystemSecrets();
  if (!isAdmin) return <AdminOnly />;
  return (
    <div className="vx-stack">
      <DataTable<SystemSecret>
        caption="Segredos da organização"
        data={list.data ?? []}
        loading={list.isLoading}
        rowKey={(r) => r.key}
        bare
        filters={[{ key: "provider", label: "Grupo", options: Object.entries(SECRET_GROUP).map(([value, label]) => ({ value, label })) }]}
        empty="Nenhuma chave no catálogo."
        columns={[
          { key: "label", header: "Segredo", cell: (r) => <span className="vx-table__primary">{r.label}<span className="vx-table__sub mono">{r.key}</span></span> },
          { key: "provider", header: "Grupo", cell: (r) => SECRET_GROUP[r.provider] ?? r.provider },
          { key: "configured", header: "Situação", cell: (r) => (r.configured ? <StatusPill tone="ok">configurado</StatusPill> : <StatusPill>vazio</StatusPill>) },
          { key: "last4", header: "Final", cell: (r) => (r.last4 ? <span className="mono">••••{r.last4}</span> : <span className="muted">-</span>) },
          { key: "updated_at", header: "Alterado", cell: (r) => (r.updated_at ? <span className="vx-table__primary"><span className="num">{formatDateTime(r.updated_at)}</span><span className="vx-table__sub">{r.updated_by_email ?? ""}</span></span> : <span className="muted">-</span>) },
        ]}
      />
      <Callout tone="info">
        Os valores ficam cifrados no banco (chave INTEGRATIONS_KEY do servidor) e nunca voltam para a tela. Gravar ou remover exige 2FA e fica na auditoria.
        O worker lê pelo banco a cada uso: trocar aqui vale em até 30 segundos, sem reiniciar nada. Quem aponta para o cofre usa a referência <span className="mono">vault:&lt;chave&gt;</span>.
      </Callout>
    </div>
  );
}
